import {Injectable} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {AppState} from "../../../store/state/app.state";
import {UmbrellaValidators} from "../../../umbrella-common/validation/umbrella-validators";
import {Country, Order} from "../../../store/state/order.state";
import {OrderActions} from "../../../store/actions/order.actions";
import {BaseOrderFormService} from "../common/base-order-form.service";
import {TranslateService} from "@ngx-translate/core";
import {ValidationMsg} from "../../../umbrella-common/services/standard-error-map";
import {StandaloneFormService} from "../../../umbrella-common/services/standalone-form.service";
import {ConfigurationProvider} from "../../../configuration/configuration.provider";
import {first} from "rxjs/operators";

@Injectable()
export class CompanyAddressFormService extends StandaloneFormService implements BaseOrderFormService {

	private FORM_CONTROL_ERROR_MSG_MAP: { [validationKey: string]: ValidationMsg[] } = {
		requiredLength: [{msg: 'validations.zip-length'}],
		pattern_us: [
			{formControlName: 'zipCode', msg: 'Zip code must contain 5 digits'}
		],
		pattern_ca: [
			{formControlName: 'zipCode', msg: 'E.g. M6K 1V2'}
		],
		pattern_pl: [
			{formControlName: 'zipCode', msg: 'validations.zip-length-pl'},
			{formControlName: 'taxId', msg: 'validations.tax-id-pl'}
		],
		pattern_nl: [
			{formControlName: 'zipCode', msg: 'validations.zip-length-nl'},
			{formControlName: 'taxId', msg: 'validations.tax-id-nl'},
			{formControlName: 'kvkNr', msg: 'validations.kvk-nr-nl'},
		],
		pattern_es: [{formControlName: 'zipCode', msg: 'validations.zip-length'}],
		pattern_mx: [{formControlName: 'zipCode', msg: 'validations.zip-length'}],
		pattern_in: [
			{formControlName: 'zipCode', msg: 'Zip code must contain 6 digits'}
		],
		pattern: [
			{formControlName: 'zipCode', msg: ''}
		]
	};

	private includedParams;

	private countries: Country[];
	private selectedCountry: Country;

	constructor(private store: Store<AppState>, private fb: FormBuilder, private translateService: TranslateService, private configProvider: ConfigurationProvider) {
		super(translateService);
	}

	protected buildFormFor(formKey: string, [order, countries, selectedCountry]: [Order, Country[], Country]) {
		this.selectedCountry = selectedCountry;
		this.countries = countries;
		const form = this.fb.group({
			companyName: this.fb.control(order.company.name, [Validators.required, UmbrellaValidators.textLengthBetween(1, 100)], null),
			street: this.fb.control(order.company.street, [Validators.required, UmbrellaValidators.textLengthBetween(1, 100)]),
			city: this.fb.control(order.company.city, [Validators.required, UmbrellaValidators.textLengthBetween(1, 100)]),
			zipCode: this.fb.control(order.company.zipCode, [Validators.required, control => UmbrellaValidators.zipCode(control, this.selectedCountry)]),
			usState: this.fb.control(order.company.state, [Validators.required]),
			country: this.fb.control(this.getCountryCode(selectedCountry.name, countries), [Validators.required])
		});

		this.configProvider.getConfigurationFor(this.selectedCountry.code).pipe(
			first()
		).subscribe((config) => {
			this.includedParams = config.clientCountryConfig.order.additionalParamsIncluded;
			Object.keys(this.includedParams).forEach(paramName => {
				if (this.includedParams[paramName]) {
					this.addAdditionalControl(form, paramName, order.company.additionalParams[paramName],
						this.selectedCountry.code, config.clientCountryConfig.order.additionalParamsPatterns[paramName]);
				}
			})
		});

		this.initFormControls(form);
		return form;
	}

	private getCountryCode(country: string, countries: Country[]): string {
		return countries.find(c => c.name == country).code;
	}

	getControlNamesInOrder(): string[] {
		return ['companyName', 'street', 'city', 'country', 'usState', 'zipCode', 'taxId', 'kvkNr'];
	}

	fillOrder(order: Order): Order {
		order.company.name = this.getValueFor('companyName', StandaloneFormService.FORM_KEY);
		order.company.street = this.getValueFor('street', StandaloneFormService.FORM_KEY);
		order.company.city = this.getValueFor('city', StandaloneFormService.FORM_KEY);
		order.company.state = this.getValueFor('usState', StandaloneFormService.FORM_KEY);
		order.company.zipCode = this.getValueFor('zipCode', StandaloneFormService.FORM_KEY);
		order.company.country = this.countries.find(country => country.code == this.getValueFor('country', StandaloneFormService.FORM_KEY)).name;
		order.clientCountry = this.getValueFor('country', StandaloneFormService.FORM_KEY);
		Object.keys(this.includedParams).forEach(paramName => {
			if ((this.includedParams)[paramName]) {
				order.company.additionalParams = {
					...order.company.additionalParams,
					[paramName]: this.getValueFor(paramName, StandaloneFormService.FORM_KEY)
				}
			}
		})
		return order;
	}

	protected override getFormSpecificErrorMap(): { [p: string]: ValidationMsg[] } {
		return this.FORM_CONTROL_ERROR_MSG_MAP;
	}

	private initFormControls(form: FormGroup) {
		this.store.dispatch(OrderActions.SET_COUNTRY({countryName: this.selectedCountry.name, code: this.selectedCountry.code}));
		form.controls['companyName'].valueChanges.subscribe(companyName => this.store.dispatch(OrderActions.SET_COMPANY_NAME({companyName})));
		form.controls['street'].valueChanges.subscribe(street => this.store.dispatch(OrderActions.SET_STREET({street})));
		form.controls['city'].valueChanges.subscribe(city => this.store.dispatch(OrderActions.SET_CITY({city})));
		form.controls['zipCode'].valueChanges.subscribe(zipCode => this.store.dispatch(OrderActions.SET_ZIP_CODE({zipCode})));
		form.controls['usState'].valueChanges.subscribe(state => this.store.dispatch(OrderActions.SET_STATE({state})));
		form.controls['country'].valueChanges.pipe().subscribe(countryCode => {
			if (this.selectedCountry.code != countryCode) {
				let statesFormControl = this.getFormControl(StandaloneFormService.FORM_KEY, 'usState');

				[statesFormControl, this.getFormControl(StandaloneFormService.FORM_KEY, 'zipCode')].forEach(control => {
					control.setValue(null);
					control.markAsDirty();
					control.markAsTouched();
					control.updateValueAndValidity({emitEvent: false});
				});

				Object.keys(this.includedParams).forEach(paramName => {
					if (this.includedParams[paramName] && !this.getFormControl(StandaloneFormService.FORM_KEY, paramName)) {
						this.addAdditionalControl(form, paramName, '', this.selectedCountry.code, null);
					} else {
						form.removeControl(paramName);
					}
				})
			}

			this.selectedCountry = this.countries.find(c => c.code == countryCode);
			const countryName = this.selectedCountry.name;
			const code = this.selectedCountry.code;
			this.store.dispatch(OrderActions.SET_COUNTRY({countryName, code}));
		});
	}

	private addAdditionalControl(form: FormGroup, paramName: string, value: string, countryCode: string, pattern: string) {
		form.addControl(paramName, this.fb.control(value, [Validators.required, control => UmbrellaValidators.additionalFieldPattern(control, countryCode, pattern)]));
		form.controls[paramName].valueChanges.subscribe(paramValue => this.store.dispatch(OrderActions.SET_COMPANY_ADDITIONAL_PARAM({paramName: paramName, value: paramValue})));
	}
}
